import { useState, useEffect } from 'react';
import { Tabs, TabsList, TabsTrigger, TabsContent } from 'src/@/components/ui/tabs';
import { Input } from 'src/@/components/ui/input';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from 'src/@/components/ui/select';
import Iconify from 'src/components/Iconify';
import { useSnackbar } from 'notistack';
import moment from 'moment';
import useAuth from 'src/hooks/useAuth';
import { NEGOCIO_ROW_LABEL } from './components/NegocioInfoRow';

import v2_actionGetCustomFieldValues from 'src/actions/v2/crm/v2_actionGetCustomFieldValues';
import v2_actionSalvarCustomFieldValue from 'src/actions/v2/crm/v2_actionSalvarCustomFieldValue';
import v2_actionUploadCustomField from 'src/actions/v2/crm/v2_actionUploadCustomField';

export default function DrawerCustomTabs({ lead, customTabs = [], onRefresh }) {
  const { enqueueSnackbar } = useSnackbar();
  const { user, cliente } = useAuth();
  const [activeTab, setActiveTab] = useState(null);
  const [valores, setValores] = useState({});
  const [loading, setLoading] = useState(true);
  const [salvando, setSalvando] = useState({});

  useEffect(() => {
    if (lead?._id) carregarValores();
  }, [lead?._id]);

  useEffect(() => {
    if (customTabs.length > 0 && !activeTab) {
      setActiveTab(customTabs[0]._id);
    }
  }, [customTabs]);

  const carregarValores = async () => {
    try {
      setLoading(true);
      const res = await v2_actionGetCustomFieldValues(lead._id);
      if (res.status === 200 && !res.data?.error) {
        const map = {};
        (res.data?.data || []).forEach(v => {
          const field = v.field_id;
          if (!field) return;
          if (field.tipo === 'texto')        map[field._id] = v.valor_texto;
          else if (field.tipo === 'selecao') map[field._id] = v.valor_selecao;
          else if (field.tipo === 'data')    map[field._id] = v.valor_data;
          else if (field.tipo === 'upload')  map[field._id] = v.valor_arquivo;
        });
        setValores(map);
      }
    } catch (err) {
      console.error('Erro ao carregar campos:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSalvarCampo = async (fieldId, valor) => {
    try {
      setSalvando(prev => ({ ...prev, [fieldId]: true }));
      await v2_actionSalvarCustomFieldValue(lead._id, fieldId, {
        valor,
        cliente_id: cliente._id,
        usuario_id: user.id
      });
      setValores(prev => ({ ...prev, [fieldId]: valor }));
    } catch (err) {
      enqueueSnackbar('Erro ao salvar campo', { variant: 'error' });
    } finally {
      setSalvando(prev => ({ ...prev, [fieldId]: false }));
    }
  };

  const handleUpload = async (fieldId, file) => {
    try {
      setSalvando(prev => ({ ...prev, [fieldId]: true }));
      const formData = new FormData();
      formData.append('arquivo', file);
      formData.append('usuario_id', user.id);
      const res = await v2_actionUploadCustomField(lead._id, fieldId, formData);
      if (res.status === 200 && !res.data?.error) {
        setValores(prev => ({ ...prev, [fieldId]: res.data.data.valor_arquivo }));
        enqueueSnackbar('Arquivo enviado', { variant: 'success' });
      }
    } catch (err) {
      enqueueSnackbar('Erro ao enviar arquivo', { variant: 'error' });
    } finally {
      setSalvando(prev => ({ ...prev, [fieldId]: false }));
    }
  };

  if (!customTabs.length) return null;

  return (
    <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
      <TabsList className="w-full flex gap-1 bg-gray-100/60 dark:bg-[#2a2d31] p-0.5 rounded-lg mb-3">
        {customTabs.map(tab => (
          <TabsTrigger
            key={tab._id}
            value={tab._id}
            className="flex-1 text-[11px] font-semibold uppercase tracking-wider px-2 py-1.5
              text-gray-500 dark:text-slate-400
              data-[state=active]:bg-white data-[state=active]:dark:bg-[#383838]
              data-[state=active]:text-gray-800 data-[state=active]:dark:text-slate-100
              data-[state=active]:shadow-sm rounded-md transition-all"
          >
            {tab.nome}
          </TabsTrigger>
        ))}
      </TabsList>

      {customTabs.map(tab => (
        <TabsContent key={tab._id} value={tab._id} className="mt-0">
          <FieldList
            fields={tab.fields}
            valores={valores}
            salvando={salvando}
            onSave={handleSalvarCampo}
            onUpload={handleUpload}
            disabled={lead?.closed}
          />
        </TabsContent>
      ))}
    </Tabs>
  );
}

function FieldList({ fields, valores, salvando, onSave, onUpload, disabled }) {
  if (!fields?.length) {
    return (
      <div className="flex items-center justify-center py-6">
        <p className="text-xs text-gray-400 dark:text-slate-500">Nenhum campo configurado</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      {fields.map(field => (
        <CustomFieldRenderer
          key={field._id}
          field={field}
          valor={valores[field._id]}
          salvando={salvando[field._id]}
          onSave={(valor) => onSave(field._id, valor)}
          onUpload={(file) => onUpload(field._id, file)}
          disabled={disabled}
        />
      ))}
    </div>
  );
}

function CustomFieldRenderer({ field, valor, salvando, onSave, onUpload, disabled }) {
  const [localValue, setLocalValue] = useState(valor || '');

  useEffect(() => { setLocalValue(valor || ''); }, [valor]);

  const handleBlur = () => {
    if (localValue !== (valor || '')) {
      onSave(localValue);
    }
  };

  // Mesma coluna de rótulo das outras seções do painel — nomes de campo longos
  // com `min-w` empurravam só a própria linha e quebravam o alinhamento.
  const labelClass = NEGOCIO_ROW_LABEL;
  const inputClass = "flex-1 h-9 border-gray-200 bg-white text-gray-900 text-sm rounded-lg hover:border-gray-300 transition-colors dark:bg-[#1A1D21] dark:border-[#383838] dark:hover:border-[#565856] dark:text-[#D1D2D3]";

  switch (field.tipo) {
    case 'texto':
      return (
        <div className="flex items-center gap-3">
          <label className={labelClass}>{field.nome}</label>
          <Input
            value={localValue}
            onChange={e => setLocalValue(e.target.value)}
            onBlur={handleBlur}
            disabled={disabled || salvando}
            placeholder="Preencher..."
            className={inputClass}
          />
        </div>
      );

    case 'selecao':
      return (
        <div className="flex items-center gap-3">
          <label className={labelClass}>{field.nome}</label>
          <Select
            value={valor || ''}
            onValueChange={val => onSave(val)}
            disabled={disabled || salvando}
          >
            <SelectTrigger className={inputClass}>
              <SelectValue placeholder="Selecione..." />
            </SelectTrigger>
            <SelectContent>
              {field.opcoes?.map(opcao => (
                <SelectItem key={opcao} value={opcao} className="text-sm">
                  {opcao}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      );

    case 'data':
      return (
        <div className="flex items-center gap-3">
          <label className={labelClass}>{field.nome}</label>
          <Input
            type="date"
            value={valor ? moment(valor).format('YYYY-MM-DD') : ''}
            onChange={e => onSave(e.target.value ? new Date(e.target.value) : null)}
            disabled={disabled || salvando}
            className={inputClass}
          />
        </div>
      );

    case 'upload':
      return (
        <div className="flex items-center gap-3">
          <label className={labelClass}>{field.nome}</label>
          {valor?.url ? (
            <div className="flex-1 flex items-center gap-2 px-3 py-2 bg-gray-50 dark:bg-[#2a2d31] rounded-lg border border-gray-200 dark:border-[#383838]">
              <Iconify icon="eva:file-text-outline" width={16} className="text-gray-400 shrink-0" />
              <a href={valor.url} target="_blank" rel="noreferrer"
                className="text-sm text-blue-600 dark:text-blue-400 hover:underline truncate flex-1">
                {valor.nome}
              </a>
            </div>
          ) : (
            <label className="flex-1 flex items-center justify-center gap-2 px-3 py-2 border border-dashed border-gray-300 dark:border-[#383838] rounded-lg cursor-pointer hover:border-[#7C3AED] hover:bg-violet-50/50 dark:hover:bg-violet-500/5 transition-colors group">
              <Iconify icon="eva:cloud-upload-outline" width={16} className="text-gray-400 group-hover:text-[#7C3AED] transition-colors" />
              <span className="text-sm text-gray-500 group-hover:text-[#7C3AED] transition-colors">Fazer upload</span>
              <input type="file" className="hidden"
                onChange={e => e.target.files[0] && onUpload(e.target.files[0])}
                disabled={disabled || salvando}
              />
            </label>
          )}
        </div>
      );

    default:
      return null;
  }
}
